import Usa from "@react-map/usa";
import { useMemo, useEffect, useState } from "react";

import { TSchool } from "../../lib/types";
import { getFullStateName } from "../../lib/helpers/stateAbbr";
import toTitleCase from "../../lib/helpers/toTitleCase";

type Props = {
  data: TSchool[];
  setStateSelected: (state: string | null) => void;
};

export default function USMap({ data, setStateSelected }: Props) {
  const [noDataMessage, setNoDataMessage] = useState<string | null>(null);

  // Get unique states that have school data (as title case full names)
  const statesWithData = useMemo(() => {
    const stateAbbrs = new Set(data.map((school) => school.state));
    return Array.from(stateAbbrs).map((abbr) =>
      toTitleCase(getFullStateName(abbr))
    );
  }, [data]);

  // Get full state names for states with data (for CSS targeting)
  const statesWithDataFullNames = useMemo(() => {
    return statesWithData; // Already in the correct format
  }, [statesWithData]);

  // Apply CSS classes to states with data
  useEffect(() => {
    const timer = setTimeout(() => {
      statesWithDataFullNames.forEach((stateName) => {
        // Find elements that start with the state name
        const elements = document.querySelectorAll(`[id^="${stateName}-"]`);
        elements.forEach((element) => {
          element.classList.add("state-with-data");
        });
      });
    }, 100); // Small delay to ensure DOM is rendered

    return () => {
      clearTimeout(timer);
      statesWithDataFullNames.forEach((stateName) => {
        const elements = document.querySelectorAll(`[id^="${stateName}-"]`);
        elements.forEach((element) => {
          element.classList.remove("state-with-data");
        });
      });
    };
  }, [statesWithDataFullNames]);

  return (
    <div className="max-w-[800px] mx-auto px-4 mt-8">
      <div className="usa-map">
        <Usa
          type="select-single"
          // @ts-expect-error - size prop typing issue
          size="auto"
          hints
          onSelect={(state) => {
            // Convert the selected state to title case full name for comparison
            let normalizedState = state;
            if (state && state.length === 2) {
              // If it's an abbreviation, convert to full name
              normalizedState = toTitleCase(getFullStateName(state));
            } else if (state) {
              // If it's already a full name, just title case it
              normalizedState = toTitleCase(state);
            }

            // Check if the selected state has data
            if (normalizedState && statesWithData.includes(normalizedState)) {
              setStateSelected(state);
              setNoDataMessage(null); // Clear any previous message
            } else if (normalizedState) {
              // State without data - show message
              setNoDataMessage(
                `No Schools with videos found in ${normalizedState}`
              );
              setStateSelected(null); // Clear state selection
            }
          }}
        />
      </div>
      {noDataMessage && (
        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex justify-between items-center">
            <p className=" font-medium">{noDataMessage}</p>
            <button
              onClick={() => setNoDataMessage(null)}
              className="text-yellow-600 hover:text-yellow-800 font-medium text-sm"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
